export const getHomePath = () => '/home';

export const AULA_LOGO_QUERY_PARAM = 'viaAulaLogo';
const AULA_LOGO_TRACKED_QUERY_PARAM = 'tracked';

/** Query params for Aula logo navigation (avoids hashHistory ?_k= from location state). */
export const AULA_LOGO_QUERY = { [AULA_LOGO_QUERY_PARAM]: '1' };

/** Held in leaveDestinationPath only until leave-without-saving confirms. */
const AULA_LOGO_LEAVE_MARKER = `${AULA_LOGO_QUERY_PARAM}=1`;

export const getAulaLogoLeaveDestination = () =>
  `${getHomePath()}?${AULA_LOGO_LEAVE_MARKER}`;

export const getAulaLogoNavPush = (options?: { tracked?: boolean }) => ({
  pathname: getHomePath(),
  query: {
    ...AULA_LOGO_QUERY,
    ...(options?.tracked ? { [AULA_LOGO_TRACKED_QUERY_PARAM]: '1' } : {}),
  },
});

type NavQuery = Record<string, string | string[] | null | undefined>;

type NavLocation =
  | {
      query?: NavQuery;
      state?: unknown;
      search?: string;
    }
  | null
  | undefined;

const getQueryParam = (
  query: NavQuery | undefined,
  key: string
): string | undefined => {
  const value = query?.[key];
  if (value == null) {
    return undefined;
  }

  return Array.isArray(value) ? value[0] : value;
};

const hasAulaLogoQueryParam = (location: NavLocation): boolean => {
  if (getQueryParam(location?.query, AULA_LOGO_QUERY_PARAM) === '1') {
    return true;
  }

  if (typeof location?.search === 'string' && location.search.length > 0) {
    const params = new URLSearchParams(location.search.replace(/^\?/, ''));
    return params.get(AULA_LOGO_QUERY_PARAM) === '1';
  }

  return false;
};

export const isAulaLogoNavLocation = (location: NavLocation): boolean =>
  hasAulaLogoQueryParam(location) || isAulaLogoNavState(location?.state);

/** @deprecated Prefer isAulaLogoNavLocation; state triggers hashHistory ?_k= params. */
export const isAulaLogoNavState = (state: unknown): boolean =>
  Boolean(
    state &&
      typeof state === 'object' &&
      (state as { viaAulaLogo?: boolean }).viaAulaLogo === true
  );

export const hasTransientHomeNavQuery = (location: NavLocation): boolean =>
  getQueryParam(location?.query, AULA_LOGO_QUERY_PARAM) === '1' ||
  getQueryParam(location?.query, AULA_LOGO_TRACKED_QUERY_PARAM) === '1';

export const getHomePathWithoutTransientNavQuery = (location: NavLocation) => {
  const query = location?.query;

  if (!query) {
    return getHomePath();
  }

  const remainingQuery = Object.entries(query).reduce<Record<string, string>>(
    (acc, [key, value]) => {
      if (
        key === AULA_LOGO_QUERY_PARAM ||
        key === AULA_LOGO_TRACKED_QUERY_PARAM ||
        value == null
      ) {
        return acc;
      }

      acc[key] = Array.isArray(value) ? value[0] : value;
      return acc;
    },
    {}
  );

  if (Object.keys(remainingQuery).length === 0) {
    return getHomePath();
  }

  return { pathname: getHomePath(), query: remainingQuery };
};

export const getTrackedHomeNavPush = () => ({
  pathname: getHomePath(),
  query: { [AULA_LOGO_TRACKED_QUERY_PARAM]: '1' },
});

export const isAulaLogoLeaveDestination = (
  path: string | null | undefined
): boolean =>
  typeof path === 'string' &&
  (path === getAulaLogoLeaveDestination() ||
    path.startsWith(`${getHomePath()}?${AULA_LOGO_LEAVE_MARKER}`));

export const getAnnouncementsPath = (spaceId: string) =>
  `/dashboard/${spaceId}/community/announcements`;

export const getCommonRoomPath = (spaceId: string) =>
  `/dashboard/${spaceId}/community/common-room`;

export const getAssignmentsPath = (spaceId: string) =>
  `/dashboard/${spaceId}/journey/assignments`;
